import React, { useState, useEffect } from "react";
import { myCustomTheme } from "../../Theme";
import { ref, onValue, remove, push } from "firebase/database";
import { db } from "../../Pages/OurProduct/firebase";

import useProducts from "../../Pages/OurProduct/hook/useProducts";
import useToast from "../../Pages/OurProduct/hook/useToast";

import Toast from "./Toast";
import Header from "./Header";
import ProductGrid from "./ProductGrid";
import AdminLogin from "./AdminLogin";
import AdminDashboard from "./AdminDashboard";
import CartPage from "./CartPage";
import WishlistPage from "./WishlistPage";
import ProductDetail from "./ProductDetail";
import CheckoutPage from "./CheckoutPage";
import PaymentPage from "./PaymentPage";
import OrderSuccess from "./OrderSuccess";

import { resetOrderId } from "../../Pages/OurProduct/orderId";

import "./style/global.css";

const OurProduct = () => {
    const { products, isLoading } = useProducts();
    const { toastMessage, showToast, clearToast } = useToast();

    const [selectedProduct, setSelectedProduct] = useState(null);
    const [showCartPage, setShowCartPage] = useState(false);
    const [showWishlistPage, setShowWishlistPage] = useState(false);
    const [checkoutPage, setCheckoutPage] = useState(false);
    const [paymentPage, setPaymentPage] = useState(false);
    const [orderSuccess, setOrderSuccess] = useState(false);
    const [showAdminLogin, setShowAdminLogin] = useState(false);
    const [isAdminLoggedIn, setIsAdminLoggedIn] = useState(false);

    const [cart, setCart] = useState([]);
    const [wishlist, setWishlist] = useState([]);

    const [mainImage, setMainImage] = useState("");
    const [userDetails, setUserDetails] = useState({
        name: "", email: "", phone: "", address: "", quantity: 1, color: "Standard Default"
    });
    const [selectedInch, setSelectedInch] = useState("6");
    const [customerDesign, setCustomerDesign] = useState(null);
    const [newReview, setNewReview] = useState({ name: "", rating: 5, comment: "" });

    const [paymentLoading, setPaymentLoading] = useState(false);
    const [currentOrderId, setCurrentOrderId] = useState("");
    const [currentInvoiceNumber, setCurrentInvoiceNumber] = useState("");
    const [deliveryDate, setDeliveryDate] = useState("");
    const [deliveryTime, setDeliveryTime] = useState("");

    const [adminUser, setAdminUser] = useState("");
    const [adminPass, setAdminPass] = useState("");
    const [currentAdminName, setCurrentAdminName] = useState("");
    const [adminSearchQuery, setAdminSearchQuery] = useState("");
    const [newProdName, setNewProdName] = useState("");
    const [newProdDescription, setNewProdDescription] = useState("");
    const [newProdType, setNewProdType] = useState("Non-Customize");
    const [newProdMrp, setNewProdMrp] = useState("");
    const [newProdDiscount, setNewProdDiscount] = useState("");
    const [newProdImagesList, setNewProdImagesList] = useState([]);
    const [newProdSelectedColors, setNewProdSelectedColors] = useState([]);
    const [newProdCategory, setNewProdCategory] = useState("");
    const [isEditMode, setIsEditMode] = useState(false);
    const [editProductId, setEditProductId] = useState(null);

    const [searchQuery, setSearchQuery] = useState("");
    const [sliderIndex, setSliderIndex] = useState(0);

    const [orders, setOrders] = useState([]);
    useEffect(() => {
        const ordersRef = ref(db, "orders");
        const unsub = onValue(ordersRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const list = Object.entries(data).map(([key, val]) => ({
                    firebaseKey: key,
                    ...val
                }));
                setOrders(list.reverse());
            } else {
                setOrders([]);
            }
        });
        return () => unsub();
    }, []);

    const [categories, setCategories] = useState([]);
    useEffect(() => {
        const catRef = ref(db, "categories");
        const unsub = onValue(catRef, (snapshot) => {
            const data = snapshot.val();
            if (data) {
                const list = Object.entries(data).map(([key, val]) => ({
                    id: key,
                    ...val
                }));
                setCategories(list);
            } else {
                setCategories([]);
            }
        });
        return () => unsub();
    }, []);

    const handleAddCategory = (name) => {
        const catRef = ref(db, "categories");
        push(catRef, { name })
            .then(() => showToast(`Category "${name}" added!`))
            .catch(() => showToast("Failed to add category."));
    };

    const handleDeleteCategory = (categoryId) => {
        remove(ref(db, `categories/${categoryId}`))
            .then(() => showToast("Category deleted."))
            .catch(() => showToast("Failed to delete category."));
    };

    const handleDeleteOrder = (firebaseKey) => {
        remove(ref(db, `orders/${firebaseKey}`))
            .then(() => showToast("Order deleted."))
            .catch(() => showToast("Failed to delete order."));
    };

    const isAnyPageOpen = () =>
        selectedProduct || showCartPage || showWishlistPage || checkoutPage ||
        paymentPage || orderSuccess || showAdminLogin || isAdminLoggedIn;

    useEffect(() => {
        if (!isAnyPageOpen() && products.length > 0) {
            const interval = setInterval(() => {
                setSliderIndex((prev) => (prev + 1) % products.length);
            }, 5000);
            return () => clearInterval(interval);
        }
    }, [products, selectedProduct, showCartPage, showWishlistPage, showAdminLogin, isAdminLoggedIn]);

    const openProduct = (product) => {
        setSelectedProduct(product);
        setMainImage(product.images && product.images.length > 0
            ? product.images[0]
            : "https://images.unsplash.com/photo-1531403009284-440f080d1e12");
        setShowCartPage(false);
        setShowWishlistPage(false);
        setCustomerDesign(null);
        setUserDetails({
            name: "", email: "", phone: "", address: "", quantity: 1,
            color: product.availableColors && product.availableColors.length > 0
                ? product.availableColors[0] : "Standard Default"
        });
    };

    const addToCart = (product, e) => {
        if (e) e.stopPropagation();
        const exists = cart.find((item) => item.id === product.id);
        if (!exists) {
            setCart([...cart, product]);
            showToast(`${product.name} added to cart!`);
            setWishlist(wishlist.filter(item => item.id !== product.id));
        } else {
            showToast(`${product.name} is already in your cart.`);
        }
    };

    const removeFromCart = (id, e) => {
        if (e) e.stopPropagation();
        setCart(cart.filter(item => item.id !== id));
        showToast("Item removed from cart.");
    };

    const addToWishlist = (product, e) => {
        if (e) e.stopPropagation();
        const exists = wishlist.find((item) => item.id === product.id);
        if (!exists) {
            setWishlist([...wishlist, product]);
            showToast(`${product.name} added to wishlist!`);
        } else {
            showToast(`${product.name} is already in your wishlist.`);
        }
    };

    const removeFromWishlist = (id, e) => {
        if (e) e.stopPropagation();
        setWishlist(wishlist.filter(item => item.id !== id));
        showToast("Item removed from wishlist.");
    };

    // ── Buy All: sets selectedProduct as { cartItems, cartImages } and opens checkout ──
    const handleBuyAll = (cartItems, cartImages) => {
        setSelectedProduct({ cartItems, cartImages });
        setShowCartPage(false);
        setCheckoutPage(true);
        setUserDetails({ name: "", email: "", phone: "", address: "", quantity: 1, color: "Standard Default" });
    };

    const handleBackToProducts = () => {
        resetOrderId();
        setOrderSuccess(false); setSelectedProduct(null);
        setCheckoutPage(false); setPaymentPage(false);
        setShowCartPage(false); setShowWishlistPage(false);
        setIsAdminLoggedIn(false);
        setCustomerDesign(null);
        setCurrentInvoiceNumber("");
        setUserDetails({ name: "", email: "", phone: "", address: "", quantity: 1, color: "Standard Default" });
    };

    const startEditProductWorkflow = (product) => {
        setIsEditMode(true); setEditProductId(product.id);
        setNewProdName(product.name); setNewProdDescription(product.description);
        setNewProdType(product.type); setNewProdMrp(product.mrp);
        setNewProdDiscount(product.discount); setNewProdImagesList(product.images || []);
        setNewProdSelectedColors(product.availableColors || []);
        setNewProdCategory(product.categoryId || "");
    };

    const cancelEditMode = () => {
        setIsEditMode(false); setEditProductId(null);
        setNewProdName(""); setNewProdDescription("");
        setNewProdType("Non-Customize"); setNewProdMrp("");
        setNewProdDiscount(""); setNewProdImagesList([]);
        setNewProdSelectedColors([]); setNewProdCategory("");
    };

    const filteredProducts = products.filter(product =>
        product.name.toLowerCase().includes(searchQuery.toLowerCase())
    );

    return (
        <div
            className="main-container"
            style={{
                fontFamily: myCustomTheme.typography.text_font,
                color: myCustomTheme.colors.p_text,
                backgroundColor: myCustomTheme.colors.bg_secondary
            }}
        >
            <Toast message={toastMessage} onClose={clearToast} />

            <div className="product-wrapper">

                {!isAnyPageOpen() && (
                    <div style={{ display: "flex", flexDirection: "column", gap: "20px", marginBottom: "40px" }}>
                        <Header
                            searchQuery={searchQuery}
                            setSearchQuery={setSearchQuery}
                            cart={cart}
                            wishlist={wishlist}
                            onCartClick={() => setShowCartPage(true)}
                            onWishlistClick={() => setShowWishlistPage(true)}
                            onAdminClick={() => setShowAdminLogin(true)}
                        />
                    </div>
                )}

                {!isAnyPageOpen() && (
                    <ProductGrid
                        products={filteredProducts}
                        isLoading={isLoading}
                        sliderIndex={sliderIndex}
                        onProductClick={openProduct}
                        onAddToCart={addToCart}
                        onAddToWishlist={addToWishlist}
                        categories={categories}
                    />
                )}

                {showAdminLogin && (
                    <AdminLogin
                        adminUser={adminUser} setAdminUser={setAdminUser}
                        adminPass={adminPass} setAdminPass={setAdminPass}
                        onClose={() => setShowAdminLogin(false)}
                        onLoginSuccess={(name) => {
                            setCurrentAdminName(name);
                            setIsAdminLoggedIn(true);
                            setShowAdminLogin(false);
                        }}
                        showToast={showToast}
                    />
                )}

                {isAdminLoggedIn && (
                    <AdminDashboard
                        currentAdminName={currentAdminName}
                        products={products}
                        orders={orders}
                        categories={categories}
                        isEditMode={isEditMode}
                        editProductId={editProductId}
                        selectedProduct={selectedProduct}
                        newProdName={newProdName} setNewProdName={setNewProdName}
                        newProdDescription={newProdDescription} setNewProdDescription={setNewProdDescription}
                        newProdType={newProdType} setNewProdType={setNewProdType}
                        newProdMrp={newProdMrp} setNewProdMrp={setNewProdMrp}
                        newProdDiscount={newProdDiscount} setNewProdDiscount={setNewProdDiscount}
                        newProdImagesList={newProdImagesList} setNewProdImagesList={setNewProdImagesList}
                        newProdSelectedColors={newProdSelectedColors} setNewProdSelectedColors={setNewProdSelectedColors}
                        newProdCategory={newProdCategory} setNewProdCategory={setNewProdCategory}
                        adminSearchQuery={adminSearchQuery} setAdminSearchQuery={setAdminSearchQuery}
                        cancelEditMode={cancelEditMode}
                        startEditProductWorkflow={startEditProductWorkflow}
                        showToast={showToast}
                        onLogout={() => setIsAdminLoggedIn(false)}
                        onDeleteOrder={handleDeleteOrder}
                        onAddCategory={handleAddCategory}
                        onDeleteCategory={handleDeleteCategory}
                    />
                )}

                {showCartPage && (
                    <CartPage
                        cart={cart}
                        onBack={() => setShowCartPage(false)}
                        onOpenProduct={openProduct}
                        onRemoveFromCart={removeFromCart}
                        onBuyAll={handleBuyAll}
                    />
                )}

                {showWishlistPage && (
                    <WishlistPage
                        wishlist={wishlist}
                        onBack={() => setShowWishlistPage(false)}
                        onOpenProduct={openProduct}
                        onRemoveFromWishlist={removeFromWishlist}
                        onAddToCart={addToCart}
                    />
                )}

                {selectedProduct && !selectedProduct.cartItems && !checkoutPage && !paymentPage && !orderSuccess && (
                    <ProductDetail
                        selectedProduct={selectedProduct}
                        setSelectedProduct={setSelectedProduct}
                        mainImage={mainImage}
                        setMainImage={setMainImage}
                        userDetails={userDetails}
                        setUserDetails={setUserDetails}
                        selectedInch={selectedInch}
                        setSelectedInch={setSelectedInch}
                        onAddToCart={addToCart}
                        onAddToWishlist={addToWishlist}
                        onBuyNow={() => setCheckoutPage(true)}
                        newReview={newReview}
                        setNewReview={setNewReview}
                        showToast={showToast}
                    />
                )}

                {checkoutPage && !paymentPage && !orderSuccess && (
                    <CheckoutPage
                        selectedProduct={selectedProduct}
                        userDetails={userDetails}
                        setUserDetails={setUserDetails}
                        customerDesign={customerDesign}
                        setCustomerDesign={setCustomerDesign}
                        onBack={() => {
                            setCheckoutPage(false);
                            if (selectedProduct?.cartItems) {
                                setShowCartPage(true);
                                setSelectedProduct(null);
                            }
                        }}
                        onContinue={() => setPaymentPage(true)}
                        showToast={showToast}
                    />
                )}

                {paymentPage && !orderSuccess && (
                    <PaymentPage
                        selectedProduct={selectedProduct}
                        userDetails={userDetails}
                        selectedInch={selectedInch}
                        customerDesign={customerDesign}
                        setCustomerDesign={setCustomerDesign}
                        paymentLoading={paymentLoading}
                        setPaymentLoading={setPaymentLoading}
                        onBack={() => setPaymentPage(false)}
                        onOrderSuccess={(orderId, invoiceNum, date, time) => {
                            setCurrentOrderId(orderId);
                            setCurrentInvoiceNumber(invoiceNum);
                            setDeliveryDate(date);
                            setDeliveryTime(time);
                            setPaymentPage(false);
                            setOrderSuccess(true);
                            // Clear cart after successful Buy All order
                            if (selectedProduct?.cartItems) {
                                setCart([]);
                            }
                        }}
                        showToast={showToast}
                    />
                )}

                {orderSuccess && (
                    <OrderSuccess
                        selectedProduct={selectedProduct}
                        userDetails={userDetails}
                        currentOrderId={currentOrderId}
                        invoiceNumber={currentInvoiceNumber}
                        deliveryDate={deliveryDate}
                        deliveryTime={deliveryTime}
                        onBack={handleBackToProducts}
                    />
                )}

            </div>
        </div>
    );
};

export default OurProduct;